import styled from 'styled-components';
import { Colors } from 'basic-ui-lib/constants';
import { Text } from 'basic-ui-lib/components/Text';
import { IcFolder, IcCheck } from 'basic-ui-lib/icons';
import { IMediaItem as ILibraryItem, MediaType } from 'modules/media/interfaces/api';
import { RenderMedia } from 'basic-ui-lib/components/screen/RenderMedia';
import { useMediaQuery } from 'react-responsive';
import colors from 'basic-ui-lib/constants/colors';
import thumbnailPlaceholder from 'assets/playlist-default.png';
interface MediaItemCardProps {
  item: ILibraryItem;
  selected?: boolean;
  onClick?: () => void;
}

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
};

const getFileExtension = (name: string): string => {
  const parts = name.split('.');
  if (parts.length > 1) {
    return parts[parts.length - 1].toUpperCase();
  }
  return '';
};

const MediaItemCard = ({ item, selected, onClick }: MediaItemCardProps) => {
  const isFolder = item.type === MediaType.FOLDER;
  // const isVideo = item.type === MediaType.VIDEO;
  const fileExt = getFileExtension(item.name);
  const fileSize = formatFileSize(item.size);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const childCount = Array.isArray(item.children) ? item.children.length : 0;

  if (isFolder) {
    return (
      <CardContainer onClick={onClick}>
        <ThumbnailWrapper $isFolder>
          <IcFolder width={64} height={64} />
        </ThumbnailWrapper>
        <ItemInfo>
          <Text body_1 semibold color="neutral.text.5" className="item-name">
            {item.name}
          </Text>
          <Text caption_3 color="neutral.text.4">
            • {childCount} item{childCount !== 1 ? 's' : ''}
          </Text>
        </ItemInfo>
      </CardContainer>
    );
  }

  return (
    <CardContainer onClick={onClick} $selected={selected}>
      <ThumbnailWrapper>
      { 
        isMobile ? (
          <img src={item.type === MediaType.IMAGE ? item.url : item.thumbnail || thumbnailPlaceholder} alt={item.name} className='thumbnail'/>
        ) : (
          RenderMedia(item, 4, false,true)
        )}
        {selected && (
          <SelectedBadge>
            <IcCheck width={28} height={28} fill={Colors.secondary[5]} />
          </SelectedBadge>
        )}
      </ThumbnailWrapper>
      <ItemInfo>
        <Text body_1 semibold color="neutral.text.5" className="item-name">
          {item.name}
        </Text>
        <MetaInfo>
          {fileExt && (
            <Text caption_3 color="neutral.text.4">
              • {fileExt}
            </Text>
          )}
          {fileSize && (
            <Text caption_3 color="neutral.text.4">
              • {fileSize}
            </Text>
          )}
        </MetaInfo>
      </ItemInfo>
    </CardContainer>
  );
};

export default MediaItemCard;

const CardContainer = styled.div<{ $selected?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  border: 2px solid ${({ $selected }) => ($selected ? Colors.primary[5] : 'transparent')};
  transition: all 0.2s ease;
  max-width: 220px;
  width: 100%;

  &:hover {
    background: ${Colors.neutral.bg[2]};
  }

  @media (max-width: 768px) {
    padding: 0px;
    border: none;
    background-color: ${colors.neutral.bg[1]};
    .item-name {
      font-weight: 500;
    }
  }
`;

const ThumbnailWrapper = styled.div<{ $isFolder?: boolean }>`
  width: 100%;
  height: 120px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: ${({ $isFolder }) => ($isFolder ? 'transparent' : Colors.neutral.bg[2])};

  @media (max-width: 768px) {
    height: 95px;
    img.thumbnail {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 4px 4px 0px 0px;
    }
  }
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  width: 100%;
  padding: 0 4px;

  .item-name {
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
`;
